/**
 * Browser-like window: title bar (sidebar toggle, back/forward, tabs), tinted sidebar, and floating content
 * cards in a resizable split. Keyboard: ⌘K/⌘L omnibox, ⌘T new tab, ⌘W close, ⌘\ split, ⌘[ ⌘] history, ⌘1–9 tabs
 * (Alt+T / Alt+W where the browser reserves ⌘T / ⌘W).
 */
import { Features, Shell, type OpenTarget, type ShellView, type ShellViewModel } from "@bundle/core";
import {
  ConnectivityBanner,
  CreateAgentSheet,
  IconButton,
  InviteGuestSheet,
  InvitePeopleSheet,
  NavigationContext,
  PaneWidthContext,
  createPaneWidth,
  ChannelToolsContext,
  MemberCardHost,
  usePreferences,
  useSession,
  usePageTree,
  useSidebar,
  useT,
  useTemplatesViewModel,
  useMail,
  useNewMessage,
  NewMessageContext,
  useViewModel,
  useWorkspace,
  useHuddlesArea,
  type ChannelOption,
  type Navigation,
} from "@bundle/ui";
import { BillingChromeBanner, BillingChromeHost, FrequentEmojiProvider, ModerationHost, RemindHost, ShareHost, WorkflowsHost, notifySendBlocked, useBilling, useFeatureAccess } from "@bundle/ui";
import { PRO_CONTACT_URL } from "./billing.ts";
import { ShortcutsSheet } from "./ShortcutsSheet.tsx";
import { HelpButton, useHelp } from "./HelpMenu.tsx";
import { BetaBanner } from "./BetaBanner.tsx";
import { NotificationsBanner } from "./NotificationsBanner.tsx";
import { useTopBanner } from "../platform/topBanner.ts";
import { useSlackShortcuts } from "./useSlackShortcuts.ts";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import type { Container } from "../platform/container.ts";
import { Omnibox, type OmniCommand } from "./Omnibox.tsx";
import { PaneView } from "./PaneView.tsx";
import { Sidebar } from "./Sidebar.tsx";
import { GetAppPrompt } from "./GetAppPrompt.tsx";
import { ActivityBar, SidebarViewContext, useShellView, type ViewBadge } from "./ActivityBar.tsx";
import { SplitView } from "./SplitView.tsx";
import { documentTitle, tabInfo } from "./tabInfo.ts";
import { TabStrip } from "./TabStrip.tsx";
import { TabBar, TabsDnd } from "./tabs/TabBar.tsx";
import { PaneDropZones } from "./tabs/TabDnd.tsx";
import { HuddleSettings, TitlebarHuddle } from "./HuddleChrome.tsx";
import { useUrlSync } from "./useUrlSync.ts";
import { ChannelChrome } from "./ChannelChrome.tsx";
import { NewMessageChrome } from "./NewMessageHost.tsx";
import { featureFlags } from "../platform/features.ts";
import { setSendRefusedHandler } from "../platform/data/mutations.ts";
import { onBillingFault } from "../platform/billingFaults.ts";

const SIDEBAR_KEY = "shell.sidebar";

const noSubscribe = () => () => {};

export function AppShell({ shell, container }: { shell: ShellViewModel; container: Container }) {
  const t = useT();
  const state = useViewModel(shell);
  const ws = useWorkspace();
  const sidebarVm = useSidebar();
  const prefs = usePreferences();
  const [sidebarOpen, setSidebarOpen] = useState(() => container.storage.get(SIDEBAR_KEY) !== "0" && window.innerWidth >= 820);
  const [omnibox, setOmnibox] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [creatingAgent, setCreatingAgent] = useState(false);
  // Channel management + sidebar folders (featureFlags.channels).
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [browsingChannels, setBrowsingChannels] = useState(false);
  const canCreateChannels = sidebarVm.state.canCreateChannels;
  const session = useSession();
  const [invitingPeople, setInvitingPeople] = useState(false);
  const welcome = session.state.welcome?.workspaceId === ws.vm.wsId ? session.state.welcome : undefined;
  const cache = ws.state.cache;
  const pages = usePageTree();
  const gallery = useTemplatesViewModel();
  const docs = pages.state.cache;
  const canCreatePages = pages.state.canCreatePages;
  const active = Shell.activeTab(state);
  const panes = Shell.panes(state.layout);
  const canSplit = state.maxPanes > 1;
  const isSplit = panes.length > 1;

  useUrlSync(shell, state, ws.slug);

  // Activity bar: Chat, Email and Contacts (mail flag on), Calendar, Huddles, Docs and Files; the view follows the active tab (core Shell.followTab).
  const mail = useMail();
  const hasMail = mail !== undefined;
  // Areas an admin switched off leave the bar (core logic/features), unless owners and admins may still look.
  const access = useFeatureAccess();
  // Huddles needs calls here (the huddles flag, a workspace with calls).
  const huddlesArea = useHuddlesArea();
  const hasHuddles = huddlesArea !== null;
  const views = useMemo<ShellView[]>(
    () => Shell.SHELL_VIEWS.filter((v) => ((v !== "email" && v !== "contacts") || hasMail) && (v !== "huddles" || hasHuddles) && (Features.VIEW_FEATURE[v] === undefined || access(Features.VIEW_FEATURE[v]) !== "none")),
    [hasMail, hasHuddles, access],
  );
  const [view, chooseView] = useShellView(container.storage, ws.vm.wsId, active.url, views);
  const mailUnread = useSyncExternalStore(mail?.mailbox.subscribe ?? noSubscribe, () => mail?.mailbox.state.unread ?? 0);
  const chatUnread = sidebarVm.state.sections.some((sec) => sec.rows.some((r) => r.type !== "page" && r.unread && !("muted" in r && r.muted)));
  const sidebarView = useMemo(() => ({ view, sidebarOpen }), [view, sidebarOpen]);
  const huddlesLive = (huddlesArea?.state.sections.live.length ?? 0) > 0;
  const viewBadges: Partial<Record<ShellView, ViewBadge>> = { chat: { count: sidebarVm.state.badge, dot: chatUnread }, email: { count: mailUnread }, huddles: { dot: huddlesLive } };
  // Slack's conversation shortcuts (⌥↑↓, ⌥⇧↑↓, Esc, ⌘⇧A) and the ⌘/ sheet.
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  // "New message" (⌘N, the Direct messages "+", "More conversations…", "Add people" on a DM).
  const newMessageVm = useNewMessage().vm;
  const [newMessageOpen, setNewMessageOpen] = useState(false);
  // Not while Chat is off, even for owners and admins who may still look.
  const canMessage = access("chat") === "full";
  const openNewMessage = useCallback(
    (prefill?: readonly string[]) => {
      if (!canMessage) return;
      newMessageVm.reset(prefill ?? []);
      setNewMessageOpen(true);
    },
    [newMessageVm, setNewMessageOpen, canMessage],
  );
  const newMessageApi = useMemo(() => ({ openNewMessage }), [openNewMessage]);
  useSlackShortcuts({ activeUrl: active.url, open: (url) => shell.open(url, "current"), onHelp: () => setShortcutsOpen((o) => !o), onNewMessage: () => openNewMessage() });
  // Conversations open in a tab or pane stay listed in Direct messages (and opening a closed DM reopens it).
  const openChannelIds = state.tabs
    .map((tab) => Shell.parseUrl(tab.url))
    .map((r) => (r?.type === "channel" || r?.type === "thread" ? r.channelId : undefined))
    .filter((id): id is string => id !== undefined)
    .toSorted()
    .join(",");
  // "You can't message this person" when a send is refused as `blocked` (Report & block); a read-only workspace
  // (`billing_required`) says so once and re-reads the plan, which swaps the composer for its notice.
  const billing = useBilling();
  const billingVm = billing?.vm;
  const [billingFault, setBillingFault] = useState<"billing_required" | "plan_limit_members" | undefined>(undefined);
  useEffect(() => {
    setSendRefusedHandler((code) => {
      if (code === "blocked") notifySendBlocked();
      if (code === "billing_required") {
        billingVm?.noteFault(code);
        setBillingFault(code);
      }
    });
    return () => setSendRefusedHandler(undefined);
  }, [billingVm]);
  // An upload past the plan's storage pool (`plan_limit_storage`): the storage-full prompt (BillingChromeHost).
  const wsId = ws.vm.wsId;
  useEffect(
    () =>
      onBillingFault((faultWs, code) => {
        if (faultWs !== wsId) return;
        billingVm?.noteFault(code);
        if (code === "billing_required") setBillingFault(code);
      }),
    [billingVm, wsId],
  );
  const onBillingPage = (() => {
    const r = Shell.parseUrl(active.url);
    return r?.type === "settings" && r.section === "billing";
  })();
  const contactPro = useCallback(() => void window.open(PRO_CONTACT_URL, "_blank", "noopener"), []);
  useEffect(() => {
    sidebarVm.vm.setOpenConversations(openChannelIds === "" ? [] : openChannelIds.split(","));
  }, [openChannelIds, sidebarVm.vm]);

  // Just created this workspace: land in #general and offer "Invite people" (until finished or skipped).
  const welcomeChannel = welcome?.generalChannelId;
  useEffect(() => {
    if (welcomeChannel !== undefined) shell.open(Shell.formatUrl({ type: "channel", ws: ws.slug, channelId: welcomeChannel }), "current");
  }, [welcomeChannel, shell, ws.slug]);
  const closeInvitePeople = useCallback(() => {
    setInvitingPeople(false);
    session.vm.finishWelcome();
  }, [session.vm]);
  const copy = useCallback((text: string) => navigator.clipboard.writeText(text), []);
  const me = cache?.members[ws.state.meId];

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((o) => {
      container.storage.set(SIDEBAR_KEY, o ? "0" : "1");
      return !o;
    });
  }, [container.storage]);
  // VS Code: another view's button shows that view (opening the sidebar); the active view's button hides or shows it.
  const selectView = useCallback(
    (v: ShellView) => {
      if (v === view) {
        toggleSidebar();
        return;
      }
      chooseView(v);
      if (!sidebarOpen) toggleSidebar();
    },
    [view, chooseView, sidebarOpen, toggleSidebar],
  );
  // ⌥⌘1–3 (Ctrl+Alt elsewhere): show a view; unlike its button, never hides the sidebar.
  const showView = useCallback(
    (v: ShellView) => {
      chooseView(v);
      if (!sidebarOpen) toggleSidebar();
    },
    [chooseView, sidebarOpen, toggleSidebar],
  );

  // Document title (Slack's format): "! " / "* " for mentions / unreads, the focused tab, the workspace.
  const title = documentTitle(active.url, cache, tabInfo(active.url, cache, t, docs, pages.state.known).title, t, { mentions: sidebarVm.state.badge, any: sidebarVm.state.unread });
  useEffect(() => {
    document.title = title;
  }, [title]);
  // Signed out / left the workspace: the tab's title and unread count leave with the shell.
  useEffect(
    () => () => {
      document.title = "Bundle";
    },
    [],
  );

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      if (mod && (key === "k" || key === "l")) {
        e.preventDefault();
        setOmnibox((o) => !o);
      } else if ((mod && key === "t") || (e.altKey && e.code === "KeyT")) {
        e.preventDefault();
        shell.newTab();
      } else if ((mod && key === "w") || (e.altKey && e.code === "KeyW")) {
        e.preventDefault();
        shell.closeTab();
      } else if (mod && e.altKey && e.code === "Backslash") {
        // ⌘⌥\: swap the sides of the split view on screen (e.key is "«" on a Mac with ⌥).
        e.preventDefault();
        shell.swapSplit(shell.state.activeTabId);
      } else if (mod && e.key === "\\") {
        e.preventDefault();
        if (e.shiftKey) shell.closePane(shell.state.focusedPaneId);
        else shell.split("row");
      } else if (mod && e.key === "[") {
        e.preventDefault();
        shell.back();
      } else if (mod && e.key === "]") {
        e.preventDefault();
        shell.forward();
      } else if (mod && e.altKey && /^Digit[1-9]$/.test(e.code)) {
        const v = views[Number(e.code.slice(5)) - 1];
        if (v !== undefined) {
          e.preventDefault();
          showView(v);
        }
      } else if (mod && /^[1-9]$/.test(e.key)) {
        e.preventDefault();
        shell.activateIndex(Number(e.key));
      } else if (mod && e.shiftKey && key === "s") {
        e.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shell, toggleSidebar, views, showView]);

  const open = useCallback((url: string, target: OpenTarget = "current") => shell.open(url, target), [shell]);
  // The Help menu (activity bar; the profile menu on phones) and the feedback sheet.
  const help = useHelp({ container, activeUrl: active.url, open, onShortcuts: () => setShortcutsOpen(true) });
  // The connection banner (shown inside the screens) keeps the top banner slot empty (platform/topBanner.ts).
  useTopBanner("offline", ws.state.banner !== undefined);

  const channels: ChannelOption[] = useMemo(
    () =>
      cache === undefined
        ? []
        : Object.values(cache.channels)
            .filter((c) => (c.kind === "public" || c.kind === "private") && cache.memberships[c.id] !== undefined && !c.archived)
            .toSorted((a, b) => a.name.localeCompare(b.name))
            .map((c) => ({ id: c.id, name: c.name })),
    [cache],
  );

  // The template gallery (featureFlags.templates): "inside" the page that is open, when there is one.
  const galleryCommands: OmniCommand[] = useMemo(() => {
    const vm = gallery;
    if (vm === null) return [];
    const run = () => {
      const route = Shell.parseUrl(Shell.activeTab(shell.getSnapshot()).url);
      vm.openGallery(route?.type === "page" ? { pageId: route.pageId } : {});
    };
    return [{ id: "new-from-template", label: t("templates.newFromTemplate"), icon: "sparkles", run }];
  }, [gallery, shell, t]);
  const commands: OmniCommand[] = useMemo(
    () => [
      { id: "new-tab", label: t("shell.newTab"), icon: "plus", run: () => shell.newTab() },
      { id: "split", label: t("shell.splitRight"), icon: "splitH", run: () => shell.split("row") },
      ...(isSplit ? [{ id: "split-swap", label: t("shell.split.swap"), icon: "sort" as const, run: () => shell.swapSplit(shell.state.activeTabId) }] : []),
      // Top-level pages and boards: not for restricted members (they add subpages to pages shared with them).
      ...(canCreatePages
        ? [
            {
              id: "new-page",
              label: t("docs.newPage"),
              icon: "pagePlus" as const,
              run: () =>
                void pages.vm.create({}).then((p) => {
                  if (p !== undefined) open(Shell.formatUrl({ type: "page", ws: ws.slug, pageId: p.id }));
                }),
            },
            {
              id: "new-board",
              label: t("projects.new"),
              icon: "board" as const,
              run: () =>
                void pages.vm.create({ template: "projects" }).then((p) => {
                  if (p !== undefined) open(Shell.formatUrl({ type: "db", ws: ws.slug, dbId: p.id }));
                }),
            },
          ]
        : []),
      ...galleryCommands,
      { id: "projects", label: t("nav.projects"), icon: "board", run: () => open(Shell.formatUrl({ type: "projects", ws: ws.slug })) },
      { id: "agents", label: t("nav.agents"), icon: "agent", run: () => open(Shell.formatUrl({ type: "agents", ws: ws.slug })) },
      { id: "search", label: t("search.title"), icon: "search", run: () => open(Shell.formatUrl({ type: "search", ws: ws.slug, query: "" })) },
      { id: "invite-people", label: t("workspace.switcher.invite"), icon: "users", run: () => setInvitingPeople(true) },
      { id: "invite", label: t("invite.guest.action"), icon: "invite", run: () => setInviting(true) },
      { id: "create-workspace", label: t("workspace.switcher.create"), icon: "plus", run: () => session.vm.startCreatingWorkspace() },
      { id: "agent", label: t("agent.create.title"), icon: "agent", run: () => setCreatingAgent(true) },
      { id: "theme", label: `${t("profile.theme")}: ${prefs.theme === "dark" ? t("profile.theme.light") : t("profile.theme.dark")}`, icon: prefs.theme === "dark" ? "sun" : "moon", run: () => prefs.setTheme(prefs.theme === "dark" ? "light" : "dark") },
      { id: "language", label: prefs.locale === "en" ? t("language.ptBR") : t("language.en"), icon: "language", run: () => prefs.setLocale(prefs.locale === "en" ? "pt-BR" : "en") },
      ...(featureFlags.channels
        ? [
            ...(canMessage ? [{ id: "new-message", label: t("newMessage.title"), icon: "edit" as const, run: () => openNewMessage() }] : []),
            ...(canCreateChannels ? [{ id: "create-channel", label: t("channel.create"), icon: "hash" as const, run: () => setCreatingChannel(true) }] : []),
            { id: "browse-channels", label: t("channels.browse.title"), icon: "search" as const, run: () => setBrowsingChannels(true) },
            { id: "new-folder", label: t("sidebar.folder.new"), icon: "folder" as const, run: () => sidebarVm.vm.startCreateFolder() },
          ]
        : []),
      ...(featureFlags.settings
        ? [
            { id: "settings-profile", label: t("settings.menu.profile"), icon: "user" as const, run: () => open(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "profile" })) },
            { id: "settings-workspace", label: t("settings.menu.workspace"), icon: "settings" as const, run: () => open(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "workspace" })) },
            { id: "settings-members", label: t("settings.nav.members"), icon: "users" as const, run: () => open(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "members" })) },
          ]
        : []),
    ],
    [t, shell, open, isSplit, ws.slug, prefs, session.vm, pages.vm, canCreatePages, canCreateChannels, sidebarVm.vm, galleryCommands, openNewMessage, canMessage],
  );

  const renderPane = (paneId: string, tabId: string) => {
    const tab = Shell.findTab(state, tabId);
    if (tab === undefined) return null;
    const focused = paneId === state.focusedPaneId;
    return (
      <PaneFrame key={paneId} paneId={paneId} tabId={tabId} url={tab.url} focused={focused} multi={panes.length > 1} shell={shell} canSplit={canSplit} />
    );
  };

  const shownTabIds = new Set(panes.map((p) => p.tabId));
  return (
    <FrequentEmojiProvider>
    <NewMessageContext.Provider value={newMessageApi}>
    <GetAppPrompt path={active.url} />
    <div className={`app ${sidebarOpen ? "app-sidebar-open" : "app-sidebar-closed"}`}>
      <TabsChrome on={featureFlags.tabs} shell={shell} state={state} cache={cache} docs={docs} knownPages={pages.state.known}>
      <header className="titlebar" data-tauri-drag-region="deep">
        <div className="titlebar-left">
          <IconButton icon="sidebar" accessibilityLabel={t("shell.toggleSidebar")} onPress={toggleSidebar} testID="toggle-sidebar" />
          <IconButton icon="chevronLeft" accessibilityLabel={t("shell.back")} onPress={() => shell.back()} disabled={active.back.length === 0} testID="nav-back" />
          <IconButton icon="chevronRight" accessibilityLabel={t("shell.forward")} onPress={() => shell.forward()} disabled={active.forward.length === 0} testID="nav-forward" />
        </div>
        {featureFlags.tabs ? (
          // Split groups, tab groups and tab management (featureFlags.tabs).
          <TabBar shell={shell} state={state} cache={cache} docs={docs} knownPages={pages.state.known} />
        ) : (
          <TabStrip
            state={state}
            cache={cache}
            docs={docs}
            knownPages={pages.state.known}
            shownTabIds={shownTabIds}
            onActivate={(id) => shell.activateTab(id)}
            onClose={(id) => shell.closeTab(id)}
            onPin={(id, pinned) => shell.pin(id, pinned)}
            onMove={(id, index) => shell.moveTab(id, index)}
          />
        )}
        <div className="titlebar-right">
          <TitlebarHuddle open={open} canSplit={canSplit} />
          <IconButton icon="plus" accessibilityLabel={t("shell.newTab")} onPress={() => shell.newTab()} testID="new-tab" />
          {canSplit ? <IconButton icon="splitH" accessibilityLabel={t("shell.splitRight")} onPress={() => shell.split("row")} testID="split" /> : null}
        </div>
      </header>
      {/* Full width under the titlebar, never under the macOS traffic lights or over the window's drag region: the
          beta banner, then one billing banner (trial, payment failed, read-only). */}
      <div className="app-banner">
        <NotificationsBanner />
        <BetaBanner onFeedback={help.openFeedback} />
        <BillingChromeBanner workspaceName={cache?.workspace.name ?? ""} hideBanner={onBillingPage} onContact={contactPro} />
      </div>
      <div className="workspace">
        <ActivityBar views={views} view={view} sidebarOpen={sidebarOpen} badges={viewBadges} onSelect={selectView} footer={<HelpButton help={help} />} />
        {sidebarOpen ? (
          <Sidebar
            view={view}
            views={views}
            onSelectView={chooseView}
            activeUrl={active.url}
            open={open}
            onSearch={() => setOmnibox(true)}
            onInvitePeople={() => setInvitingPeople(true)}
            onInviteGuest={() => setInviting(true)}
            onCreateAgent={() => setCreatingAgent(true)}
            onCreateChannel={() => setCreatingChannel(true)}
            onBrowseChannels={() => setBrowsingChannels(true)}
            canSplit={canSplit}
            help={help}
          />
        ) : null}
        <main className="content">
          {ws.state.cache === undefined && ws.state.banner !== undefined ? <ConnectivityBanner banner={ws.state.banner} /> : null}
          <ChannelToolsContext.Provider value={featureFlags.channels}>
            <SidebarViewContext.Provider value={sidebarView}>
              {featureFlags.settings ? (
                // Member profile cards (click a name or avatar), shown over the panes.
                <MemberCardHost onOpenUrl={(url) => open(url)}>
                  <SplitView node={state.layout} renderPane={renderPane} onRatio={(path, ratio) => shell.setRatio(path, ratio)} />
                </MemberCardHost>
              ) : (
                <SplitView node={state.layout} renderPane={renderPane} onRatio={(path, ratio) => shell.setRatio(path, ratio)} />
              )}
            </SidebarViewContext.Provider>
          </ChannelToolsContext.Provider>
        </main>
      </div>
      </TabsChrome>
      <Omnibox open={omnibox} onOpenChange={setOmnibox} navigate={open} commands={commands} canSplit={canSplit} />
      <HuddleSettings open={open} />
      <RemindHost />
      <ModerationHost />
      <BillingChromeHost workspaceName={cache?.workspace.name ?? ""} onContact={contactPro} {...(billingFault === undefined ? {} : { fault: billingFault })} onFaultClose={() => setBillingFault(undefined)} />
      <ShareHost />
      <WorkflowsHost />
      <ShortcutsSheet open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      {help.host}
      <NewMessageChrome open={newMessageOpen} onClose={() => setNewMessageOpen(false)} navigate={open} />
      {featureFlags.channels ? (
        <ChannelChrome
          creating={creatingChannel}
          browsing={browsingChannels}
          onCloseCreate={() => setCreatingChannel(false)}
          onCloseBrowse={() => setBrowsingChannels(false)}
          onCreate={() => {
            setBrowsingChannels(false);
            setCreatingChannel(true);
          }}
          open={open}
        />
      ) : null}
      <InviteGuestSheet
        open={inviting}
        onClose={() => setInviting(false)}
        channels={channels}
        now={ws.vm.deps.clock.now()}
        onSubmit={async (req) => (await ws.vm.inviteGuest(req)).invite?.url}
        onCopy={copy}
      />
      <InvitePeopleSheet
        open={invitingPeople || welcome !== undefined}
        onClose={closeInvitePeople}
        workspaceName={cache?.workspace.name ?? ""}
        canInviteAdmins={me?.role === "owner" || me?.role === "admin"}
        onCreate={(req) => ws.vm.createInvites(req)}
        onCopy={copy}
        onboarding={welcome !== undefined}
        onContact={contactPro}
      />
      <CreateAgentSheet
        open={creatingAgent}
        onClose={() => setCreatingAgent(false)}
        channels={channels}
        onSubmit={async (req) => (await ws.vm.createAgent(req)).token}
      />
    </div>
    </NewMessageContext.Provider>
    </FrequentEmojiProvider>
  );
}

/** Tab drag and drop around the title bar and panes (featureFlags.tabs); off, just the children. */
function TabsChrome({ on, children, ...p }: { on: boolean; children: ReactNode } & Parameters<typeof TabsDnd>[0]) {
  return on ? <TabsDnd {...p}>{children}</TabsDnd> : <>{children}</>;
}

function PaneFrame(p: { paneId: string; tabId: string; url: string; focused: boolean; multi: boolean; shell: ShellViewModel; canSplit: boolean }) {
  const { shell, paneId, tabId } = p;
  const reselected = useSyncExternalStore(shell.subscribeReselect, () => shell.reselectCount(tabId));
  const nav = useMemo<Navigation>(
    () => ({
      open: (url, target = "current") => {
        // Navigating inside a pane targets that pane's tab.
        if (shell.state.focusedPaneId !== paneId) shell.focusPane(paneId);
        shell.open(url, target);
      },
      replace: (url) => {
        if (shell.state.focusedPaneId !== paneId) shell.focusPane(paneId);
        shell.replace(url);
      },
      back: () => shell.back(),
      forward: () => shell.forward(),
      close: () => shell.closeTab(tabId),
      focused: p.focused,
      canSplit: p.canSplit,
      reselected,
    }),
    [shell, paneId, tabId, p.focused, p.canSplit, reselected],
  );
  // The pane's own width (usePaneWidthAtLeast): a split pane is narrow on a wide window, and it changes while a divider
  // is dragged. The screen renders once the pane is measured, so its first frame already has the right layout.
  const section = useRef<HTMLElement>(null);
  const [width] = useState(createPaneWidth);
  const [measured, setMeasured] = useState(false);
  useLayoutEffect(() => {
    const el = section.current;
    if (el === null) return undefined;
    width.set(el.clientWidth);
    setMeasured(true);
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => width.set(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  return (
    <section
      ref={section}
      className={`pane ${p.focused && p.multi ? "pane-focused" : ""}`}
      data-testid={`pane-${paneId}`}
      onPointerDownCapture={() => {
        if (!p.focused) shell.focusPane(paneId);
      }}
    >
      <NavigationContext.Provider value={nav}>
        <PaneWidthContext.Provider value={width}>{measured ? <PaneView url={p.url} /> : null}</PaneWidthContext.Provider>
      </NavigationContext.Provider>
      {featureFlags.tabs ? <PaneDropZones paneId={paneId} tabId={tabId} shell={shell} /> : null}
    </section>
  );
}
